import React, {useCallback, useEffect, useMemo, useState} from "react";
import { CasingData } from "../../../../types/CasingData";
import {fetchSelectOptionsByContractNO} from "../../../../backendInteraction/ApiFetch";
import {depthValueValidator} from "../../../../utils/Validator";
import {
    addCasingRow,
    deleteCasingByCasid,
    fetchCasingByPid,
    updateCasingRow
} from "../../../../backendInteraction/CasingApiFetc";
import {Button, Input, message, Select, Space, Table} from "antd";
import {ColumnsType} from "antd/es/table";

interface CasingTableProps {
    data: CasingData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
    holeId: string;
    setHoleId: (value: string) => void;
}

const CassingTable: React.FC<CasingTableProps> = ({ data, plodId, contractNo, isView, isAddNewRow, setIsAddNewRow, holeId, setHoleId }) => {
    const [casingData, setCasingData] = useState<CasingData[]>([]);
    const [typeOptions, setTypeOptions] = useState<{ value: string; label: string }[]>([]);
    const [sizeOptions, setSizeOptions] = useState<{ value: string; label: string }[]>([]);
    const [editingCasId, setEditingCasId] = useState<number | null>(null);
    const [editData, setEditData] = useState<CasingData | null>(null);

    const loadCasing = useCallback(async () => {
        if (contractNo) {
            const casingType = await fetchSelectOptionsByContractNO(contractNo, "CasingType");
            const casingSize = await fetchSelectOptionsByContractNO(contractNo, "CasingSize");
            setTypeOptions(casingType.map(type => ({ value: type, label: type })));
            setSizeOptions(casingSize.map(size => ({ value: size, label: size })));
        } else {
            setTypeOptions([]);
            setSizeOptions([]);
        }
    }, [contractNo]);

    useEffect(() => {
        loadCasing();
    }, [loadCasing]);

    useEffect(() => {
        setCasingData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingCasId !== -1) {
            const len = casingData.length;
            const newRow: CasingData = {
                key: -1,
                cas_id: -1,
                hole_id: len > 0 ? casingData[len - 1].hole_id || holeId : holeId,
                casing_type: "",
                casing_size: "",
                depth_from: len > 0 ? casingData[len - 1].depth_to : "",
                depth_to:"",
                pid: plodId ?? -1,
            };
            setCasingData(prevData => {
                const hasNewRow = prevData.some(item => item.cas_id === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            });

            setEditingCasId(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingCasId]);

    useEffect(() => {
        if (isView) {
            if (editingCasId === -1) {
                setCasingData(prevData => prevData.filter(item => item.cas_id !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingCasId(null);
            setEditData(null);
        }
    }, [isView]);

    const saveChanges = useCallback(async () => {
        console.log("Saving changes:", editData);

        if (!editData) return;

        const { depth_from, depth_to, hole_id } = editData;

        if(!depthValueValidator(depth_from, depth_to, false)) return;

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingCasId === -1) {
            const success = await addCasingRow(editData, aid);
            if (success) {
                setCasingData(await fetchCasingByPid(plodId ?? -1, aid));
                message.success("New casing added successfully!");
            } else {
                message.error("Failed to add new casing.");
            }
        } else {
            const success = await updateCasingRow(editingCasId ?? -1, editData);
            if (success) {
                setCasingData(await fetchCasingByPid(plodId ?? -1, aid));
                message.success("Casing updated successfully!");
            } else {
                message.error("Failed to update casing.");
            }
        }

        !(hole_id === holeId) && setHoleId(hole_id);

        if (editingCasId === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingCasId(null);
        setEditData(null);
    }, [editData, editingCasId, setIsAddNewRow]);

    const cancelEditing = useCallback((record: CasingData) => {
        console.log("Cancel editing");
        setEditingCasId(null);
        setEditData(null);

        if (record.cas_id === -1) {
            setCasingData(prevData => prevData.filter(item => item.cas_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: CasingData) => {
        console.log("Editing row:", record);

        if (editingCasId === -1) {
            setCasingData(prevData => prevData.filter(item => item.cas_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingCasId(prevId => {
            if (prevId !== null) cancelEditing({ cas_id: prevId } as CasingData);
            return record.cas_id;
        });

        setEditData({ ...record });
    }, [editingCasId, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: CasingData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const success = await deleteCasingByCasid(record.cas_id);
        if (success) {
            setCasingData(await fetchCasingByPid(plodId ?? -1, aid));
            message.success("Casing deleted successfully!");
        } else {
            message.error("Failed to delete casing.");
        }
    }, []);

    const handleInputChange = useCallback((key: keyof CasingData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);

    const columns: ColumnsType<CasingData> = useMemo(() => [
        {
            title: "Hole ID",
            dataIndex: "hole_id",
            key: "hole_id",
            width: "16%",
            render: (_, record) =>
                editingCasId === record.cas_id ? (
                    <Input
                        value={editData?.hole_id}
                        onChange={(e) => handleInputChange("hole_id", e.target.value)}
                    />
                ) : (
                    record.hole_id
                ),
        },
        {
            title: "Type",
            dataIndex: "casing_type",
            key: "casing_type",
            width: "16%",
            minWidth: 280,
            render: (_, record) =>
                editingCasId === record.cas_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={typeOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.casing_type || ""}
                        onChange={(value) => handleInputChange("casing_type", value || "")}
                    />
                ) : (
                    record.casing_type
                ),
        },
        {
            title: "Size",
            dataIndex: "casing_size",
            key: "casing_size",
            width: "16%",
            minWidth: 280,
            render: (_, record) =>
                editingCasId === record.cas_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={sizeOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.casing_size || ""}
                        onChange={(value) => handleInputChange("casing_size", value || "")}
                    />
                ) : (
                    record.casing_size
                ),
        },
        {
            title: "From",
            dataIndex: "depth_from",
            key: "depth_from",
            width: "20%",
            render: (_, record) =>
                editingCasId === record.cas_id ? (
                    <Input
                        value={editData?.depth_from}
                        onChange={(e) => handleInputChange("depth_from", e.target.value)}
                    />
                ) : (
                    record.depth_from
                ),
        },
        {
            title: "To",
            dataIndex: "depth_to",
            key: "depth_to",
            width: "20%",
            render: (_, record) =>
                editingCasId === record.cas_id ? (
                    <Input
                        value={editData?.depth_to}
                        onChange={(e) => handleInputChange("depth_to", e.target.value)}
                    />
                ) : (
                    record.depth_to
                ),
        },
        ...(!isView
            ? [
                {
                    title: "Action",
                    key: "action",
                    width: "20%",
                    render: (_, record) => (
                        <Space size="small">
                            <Button color="geekblue" variant="text"
                                    onClick={() => (editingCasId === record.cas_id ? saveChanges() : startEditing(record))}>
                                {editingCasId === record.cas_id ? "Save" : "Edit"}
                            </Button>
                            <Button color="geekblue" variant="text"
                                    onClick={() => (editingCasId === record.cas_id ? cancelEditing(record) : deleteRow(record))}>
                                {editingCasId === record.cas_id ? "Cancel" : "Delete"}
                            </Button>
                        </Space>
                    ),
                },
            ] as ColumnsType<CasingData>
            : []),
    ], [isView, editingCasId, typeOptions, sizeOptions, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<CasingData>
            columns={columns}
            pagination={false}
            dataSource={casingData}
        />
    );
};

export default CassingTable;
